import { MouseEventHandler, RefObject, useCallback, useEffect, useMemo, useRef, useState } from 'react';
import PhotoIconSVG from "@/public/PhotoButtonMark1.svg";
import styled from 'styled-components';
import CMSImage, { CMSCroppedImage } from 'components/images/Image';
import { Navigation, Pagination } from "swiper/modules";
import { Swiper, SwiperSlide } from "swiper/react";
import SCloseButton from 'components/framework/SCloseButton';

import "swiper/css";
import "swiper/css/navigation";
import "swiper/css/pagination";
import { ICustomImage } from "@filmweb/sanity-types";
import { getOrgSize } from "lib/utils/getOrgSize";



//#region [Props]
type GalleryContainerProps = {
	images?: ICustomImage[];
	overridenImage?: ICustomImage;
};
//#endregion

//#region [Component]
export default function GalleryContainer({ overridenImage, images }: GalleryContainerProps) {
	const [showGallery, setShowGallery] = useState(false);

	useEffect(() => {
		if (showGallery) {
			document.body.style.overflow = "hidden";
		} else {
			document.body.style.overflow = "";
		}
	}, [showGallery]);

	const imageArray = useMemo(() => {
		if ((images?.length ?? 0) === 0) {
            console.debug("Images length === 0");
			return null;
		}
		// filter out all non-horizontal images
		const imgs = images!.filter(img => {
			if (!img?.asset?._id) {
				return false;
			}
			const orgSize = getOrgSize(img.asset._id);
			if (orgSize.aspect <= 1) {
				return false;
			}
			if (overridenImage?._id === img.asset._id) {
				return false;
			}
			return true;
		})

		if (overridenImage) {
			if ((imgs?.length ?? 0) === 0) {
				return [overridenImage];
			}
			return [overridenImage, ...imgs!];
		}
		if ((imgs?.length ?? 0) < 2) {
			// there is 1 or 0 images. Return as is
			return imgs;
		}

		if ((imgs?.length ?? 0) < 3) {
			// there is 1 or 0 images. Return as is
			return [imgs![1], imgs![0]]; // re-arrange the first 2 images
		}

		return [imgs![1], imgs![0], ...imgs!.slice((imgs!.length - 2) * -1)]; // re-arrange the first 2 images
	}, [images, overridenImage]);

	const containerRef = useRef<HTMLDivElement>();

	const onClose = useCallback<MouseEventHandler>((event: React.MouseEvent<HTMLButtonElement>) => {
		event.preventDefault();
		if ((event.target as HTMLElement).tagName === "BUTTON" || event.target === containerRef.current) {
			setShowGallery(false);
		}
	}, [containerRef]);

	if ((imageArray?.length ?? 0) === 0) {
		return null;
	}

	return (<>
		<SGalleryButton onClick={() => setShowGallery(true)} title="Vis bildegalleri" type='button'>
			<CMSCroppedImage image={imageArray![0]} width={400} heightRatio={1} isFilmweb />
			<PhotoIconSVG />
		</SGalleryButton>
		{showGallery && <SGalleryContainer ref={containerRef as RefObject<HTMLDivElement>} onClick={onClose}>
			<SGalleryPositioningContainer>
				<SGalleryCloser onClick={onClose} $isOpen />
				<SSwiper modules={[Navigation, Pagination]} navigation pagination loop>
					{imageArray!.map(image => <SwiperSlide key={image.asset._id}>
						<SGalleryImage image={image} sizes="(min-width: 1440px) 1440px, 100vw" isFilmweb />
					</SwiperSlide>)}
				</SSwiper>
			</SGalleryPositioningContainer>
		</SGalleryContainer>}
	</>);

}
//#endregion

//#region [Styles]
const SGalleryButton = styled.button`
	display: block;
	padding: 0;
	margin: 0 auto;
	background: none;
	border: none;
	border-radius: 50%;
	max-width: 50vw;
	aspect-ratio: 1;
	overflow: hidden;
	position: relative;
	color: white;
	width: 100%;

	img {
		width: 100%;
		height: auto;
	}

	@media screen and (min-width: 641px) {
		max-width: 25rem;
	}

	svg {
		position: absolute;
		top: 50%;
		left: 50%;
		transform: translate(-50%, -50%);
		width: 40%;
		z-index: 1;
	}
`;

const SGalleryContainer = styled.div`
	--swiper-theme-color: #fff;
	--swiper-pagination-bullet-inactive-color: #fff;

	position: fixed;
	z-index: 3;
	top: 0;
	left: 0;
	width: 100vw;
	height: 100vh;
	background: #000000;
	background: rgba(0, 0, 0, 0.9);
`;

const SGalleryPositioningContainer = styled.div`
	position: absolute;
	z-index: 5;
	top: 50%;
	left: 50%;
	width: 100%;
	max-width: 80vw;
	transform: translate(-50%, -50%);
`;

const SGalleryImage = styled(CMSImage)`
	max-width: 80vw;
	width: 100%;
	height: auto;
`;

const SSwiper = styled(Swiper)`
	.swiper-wrapper {
		align-items: center;
	}
`;

const SGalleryCloser = styled(SCloseButton)`
	--color_menu_text: #fff;
	margin-left: auto;
	cursor: pointer;
	display: block;
`;



//#endregion